import { App, Button, Card, Segmented, Select, Space, Typography, theme as antdTheme } from 'antd';
import { CheckCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { PageHeader } from '@/shared/components/page-header';
import { SectionCard } from '@/shared/components/ui';
import { useThemeStore } from '@/store/theme';
import { usePrefsStore } from '@/store/prefs';
import { themeList } from '@/themes';
import { navItems } from '@/app/nav-config';
import { useAbility } from '@/auth/ability-provider';
import { Cheatsheet } from '@/app/cheatsheet';
import { useState } from 'react';

export function PreferencesPage() {
  const { message } = App.useApp();
  const { token } = antdTheme.useToken();

  const themeKey = useThemeStore((s) => s.themeKey);
  const setTheme = useThemeStore((s) => s.setTheme);
  const density = useThemeStore((s) => s.density);
  const setDensity = useThemeStore((s) => s.setDensity);

  const defaultLanding = usePrefsStore((s) => s.defaultLandingPath);
  const setDefaultLanding = usePrefsStore((s) => s.setDefaultLandingPath);

  const ability = useAbility();
  const landingOptions = [
    { value: '', label: 'Dashboard (default)' },
    ...navItems
      .filter((item) => !item.can || ability.can(item.can.action, item.can.subject))
      .map((item) => ({ value: item.path, label: item.label })),
  ];

  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Account"
        title="Preferences"
        description="Personal settings stored only in this browser. Clearing site data on this device will reset them."
      />

      <SectionCard
        eyebrow="Appearance"
        title="Theme & density"
        subtitle="Visual style of the app. Independent of your account — each device remembers its own choice."
      >
        <Space orientation="vertical" size={18} style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Density
            </Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Segmented
                value={density}
                onChange={(v) => setDensity(v as 'comfortable' | 'compact')}
                options={[
                  { label: 'Comfortable', value: 'comfortable' },
                  { label: 'Compact', value: 'compact' },
                ]}
              />
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
              Compact tightens table rows and form spacing.
            </Typography.Text>
          </div>

          <div>
            <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Theme
            </Typography.Text>
            <div
              style={{
                marginTop: 10,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 10,
              }}
            >
              {themeList.map((t) => {
                const selected = t.key === themeKey;
                const accent = (t.config.token?.colorPrimary as string) ?? '#5b9bff';
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTheme(t.key)}
                    aria-pressed={selected}
                    style={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      padding: '10px 12px',
                      background: token.colorBgContainer,
                      border: `1px solid ${selected ? accent : token.colorBorderSecondary}`,
                      outline: selected ? `2px solid ${accent}` : 'none',
                      outlineOffset: selected ? 1 : 0,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        width: 14, height: 14, borderRadius: 4,
                        background: accent, flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: token.colorTextTertiary }}>
                        {t.mode === 'dark' ? 'Dark' : 'Light'} · {t.character}
                      </div>
                    </div>
                    {selected && <CheckCircleFilled style={{ color: accent, fontSize: 16 }} />}
                  </button>
                );
              })}
            </div>
          </div>
        </Space>
      </SectionCard>

      <SectionCard
        eyebrow="Navigation"
        title="Default landing page"
        subtitle="Where you land after signing in. Switch this if Dashboard isn't what you reach for first."
      >
        <Space orientation="vertical" size={10} style={{ width: '100%' }}>
          <Select
            style={{ width: '100%', maxWidth: 360 }}
            value={defaultLanding}
            onChange={(v) => {
              setDefaultLanding(v);
              message.success('Landing page updated');
            }}
            options={landingOptions}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Only pages you have access to appear in this list. Removing a permission would silently fall back to Dashboard.
          </Typography.Text>
        </Space>
      </SectionCard>

      <SectionCard
        eyebrow="Productivity"
        title="Keyboard shortcuts"
        subtitle="Same set the rest of the app uses — press ? anywhere to peek without leaving the page."
        extra={
          <Button icon={<ReloadOutlined rotate={0} />} onClick={() => setCheatsheetOpen(true)}>
            View shortcuts
          </Button>
        }
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Use <kbd>?</kbd> for the cheatsheet, <kbd>⌘</kbd>+<kbd>K</kbd> for the command palette,
          and <kbd>g</kbd> followed by a letter (e.g. <kbd>g</kbd> <kbd>s</kbd>) to jump to a module.
        </Typography.Text>
      </SectionCard>

      <Cheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />
    </div>
  );
}
